import { Injectable } from "@angular/core";
import { firstValueFrom, of } from "rxjs";
import { catchError, map } from "rxjs/operators";
import {
  getSalesforceWebTransport,
  SALESFORCE_WEB_CONFIG,
} from "@app/config/salesforce.config";
import { Response } from "@app/models/response";
import { SalesforceWebDirectService } from "./salesforce-web-direct.service";
import { SalesforceWebAttributes } from "./salesforce-web.types";
import { SharedService } from "./shared.service";

export type { SalesforceWebAttributes };

/** Estilo dos logs Salesforce — laranja, consistente em todo o app. */
const SF_LOG_STYLE = "color: #FF8C00; font-weight: bold;";
const SF_LOG_WARN_STYLE = "color: #FFA500; font-weight: bold;";

/**
 * Web: roteia eventos conforme getSalesforceWebTransport().
 *
 * - SF-WEB-PRODUCTION → trackEvent() POST Supermup (eventsPath)
 * - SF-WEB-PROVISIONAL → SalesforceWebDirectService (REST + proxy local)
 *
 * Mapa: docs/SALESFORCE_INTEGRATION.md
 */
@Injectable({
  providedIn: "root",
})
export class SalesforceWebEventsService {
  /**
   * Um 404 avisa uma vez POR ENDPOINT. Com uma flag unica, o primeiro 404 (contato)
   * calava o aviso do outro endpoint (evento) — some justamente o diagnostico util.
   */
  private readonly endpointMissingLogged = new Set<string>();

  constructor(
    private shared: SharedService,
    private direct: SalesforceWebDirectService
  ) {}

  /**
   * Dispara evento customizado (sf_* ou login/logout) via API.
   */
  async trackEvent(eventName: string, attributes: SalesforceWebAttributes = {}): Promise<boolean> {
    if (!SALESFORCE_WEB_CONFIG.enabled) {
      return false;
    }

    if (getSalesforceWebTransport() === "log-only") {
      return true;
    }

    // SF-WEB-PROVISIONAL — não remover: caminho de teste local até API Supermup existir
    if (getSalesforceWebTransport() === "direct") {
      const contactKey = this.shared.user?.clientId ?? null;
      return this.direct.trackEvent(eventName, contactKey, attributes);
    }

    // SF-WEB-PRODUCTION — contrato docs/SALESFORCE_WEB.md
    const payload = {
      event_name: eventName,
      contact_key: this.shared.user?.clientId ?? null,
      attributes,
      platform: "web",
      sent_at: new Date().toISOString(),
    };

    try {
      const ok = await firstValueFrom(
        this.shared.post<unknown>(SALESFORCE_WEB_CONFIG.eventsPath, payload).pipe(
          map((response) =>
            this.isSuccessfulResponse(response, "evento", eventName, SALESFORCE_WEB_CONFIG.eventsPath)
          ),
          catchError((err) => {
            this.logTransportError("evento", eventName, err, SALESFORCE_WEB_CONFIG.eventsPath);
            return of(false);
          })
        )
      );
      if (ok) {
        console.log(`%c[Salesforce Web] Evento enviado: ${eventName}`, SF_LOG_STYLE, attributes);
      }
      return ok;
    } catch (err) {
      this.logTransportError("evento", eventName, err, SALESFORCE_WEB_CONFIG.eventsPath);
      return false;
    }
  }

  /**
   * Sincroniza contact key e atributos do contato (equivalente ao setContactKey + setAttributes nativo).
   */
  async syncContact(contactKey: string, attributes: SalesforceWebAttributes): Promise<boolean> {
    if (!SALESFORCE_WEB_CONFIG.enabled || !SALESFORCE_WEB_CONFIG.contactSyncPath) {
      return true;
    }

    if (getSalesforceWebTransport() === "direct") {
      return this.direct.syncContact();
    }

    const payload = {
      contact_key: contactKey,
      attributes,
      platform: "web",
      sent_at: new Date().toISOString(),
    };

    try {
      return await firstValueFrom(
        this.shared.post<unknown>(SALESFORCE_WEB_CONFIG.contactSyncPath, payload).pipe(
          map((response) =>
            this.isSuccessfulResponse(response, "contato", contactKey, SALESFORCE_WEB_CONFIG.contactSyncPath)
          ),
          catchError((err) => {
            this.logTransportError("contato", contactKey, err, SALESFORCE_WEB_CONFIG.contactSyncPath);
            return of(false);
          })
        )
      );
    } catch (err) {
      this.logTransportError("contato", contactKey, err, SALESFORCE_WEB_CONFIG.contactSyncPath);
      return false;
    }
  }

  /**
   * SharedService não lança erro em 4xx — devolve { status: false, httpStatus }.
   * Só consideramos sucesso quando a API respondeu OK e status === true.
   */
  private isSuccessfulResponse(
    response: Response<unknown>,
    kind: string,
    label: string,
    path: string
  ): boolean {
    const httpStatus = response.httpStatus ?? 0;
    if (!response.status || httpStatus >= 400) {
      this.logTransportError(kind, label, { status: httpStatus }, path);
      return false;
    }
    return true;
  }

  private logTransportError(kind: string, label: string, err: unknown, path: string): void {
    const status = (err as { status?: number })?.status;
    if (status === 404 && !this.endpointMissingLogged.has(path)) {
      this.endpointMissingLogged.add(path);
      console.warn(
        `%c[Salesforce Web] API não encontrada (${path}). ` +
          "O backend precisa expor o proxy SFMC para eventos na web.",
        SF_LOG_WARN_STYLE
      );
      return;
    }
    console.warn(`%c[Salesforce Web] Falha ao enviar ${kind} "${label}"`, SF_LOG_WARN_STYLE, err);
  }
}
